import * as XLSX from "xlsx";
import {saveAs} from "file-saver";
import {HttpService} from "../../services/http/Http.service";
import {message} from "antd";
import {SimulatedFileTS, SpringPageable, StatusCodeE} from "../../model/models";
import {UtilsService} from "../../services/Utils.service";

export interface CallRegistryReportDTO {
    userId: number;
    count: number;
    operatorName: string;
    userFiscalCode: string;
    userFirstName: string;
    userLastName: string;
    callDateTime: string;
    isDone: boolean;
}

export class PracticeExportService{
    static loadAndExportToExcel = (simulatorId:number, status: StatusCodeE, filename: string = "table_data.xlsx"): Promise<void> => {
        const requestPage: SpringPageable = {
            page: 0,
            size: 500000
        }
        return HttpService.getInstanceAxios().get(`/simulazione/${simulatorId}/file-ts/status/${status}/all`, {
            params: requestPage
        }).then(response => {
            const combinatorContent = response.data.content
            this.exportToExcel(combinatorContent, filename);
        }).catch(() => {
            message.error("Si è verificato un errore durante l'esportazione di Excel")
        })
    };

    private static exportToExcel = (data: SimulatedFileTS[], filename: string = "table_data.xlsx") => {
        const excelData: any[] = []
        data.forEach(d => {
            excelData.push({
                CODICE_FISCALE: d.fiscalCode,
                COGNOME_NOME: d.fullName,
                ANNO: d.years.sort((a, b) => parseInt(a) - parseInt(b)).join(" - "),
                CODICE_ESENZIONE: d.exemptionCodes.join(' - '),
                TIPO: d.type,
                RATE: d.hasInstallment ? "Si" : "No",
                PAGOPA: d.hasPayment ? "Si" : "No",
                TICKET: d.totalTicket - d.totalSanction,
                INTERESSI: d.totalInterest ?? 0,
                SANZIONE: d.totalSanction ?? 0,
                SPESE: d.totalSpese,
                IMPORTO_TOTALE: d.totalSum ?? 0,
            })
        })

        const worksheet = XLSX.utils.json_to_sheet(excelData);
        const currencyColumns = ["H", "I", "J", "K", "L"]; // excel columns to which apply the format

        currencyColumns.forEach((col) => {
            for (let row = 2; row <= excelData.length + 1; row++) {
                const cell = worksheet[`${col}${row}`];
                if (cell) {
                    cell.t = "n";
                    cell.z = '€ #,##0.00';
                }
            }
        });

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");

        const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
        const fileData = new Blob([excelBuffer], { type: "application/octet-stream" });

        saveAs(fileData, filename);
    };

    static loadAndExportToExcelReport(from: string, to: string, filename: string = "report_chiamate.xlsx") {
        HttpService.getInstanceAxios()
            .get(`/registry/report?from=${from}&to=${to}`)
            .then(response => {
                const reportData: CallRegistryReportDTO[] = response.data;
                this.exportToExcelReport(reportData, filename);
            })
            .catch(() => {
                message.error("Si è verificato un errore durante l'esportazione di Excel");
            });
    }

    private static exportToExcelReport(data: CallRegistryReportDTO[], filename: string) {
        const excelData = data.map(d => ({
            "Dipendente ATS": d.operatorName,
            "Data e ora della telefonata": UtilsService.formatDateHour(d.callDateTime),
            "Stato della richiesta": d.isDone ? "Evasa" : "Da evadere",
            "Codice fiscale assistito": d.userFiscalCode,
            "Nome e cognome assistito": `${d.userFirstName} ${d.userLastName}`,
        }));

        const worksheet = XLSX.utils.json_to_sheet(excelData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Report");

        const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
        const fileBlob = new Blob([excelBuffer], { type: "application/octet-stream" });

        saveAs(fileBlob, filename);
    }

    /**
     * Scarica il report rate generato dal BE. Filtri e flag devono essere gli stessi della griglia,
     * altrimenti il file contiene un insieme di righe diverso da quello a schermo. Gli errori
     * risalgono al chiamante, che li mostra con HttpErrorService.
     */
    static async loadAndExportToExcelRate(
        payload: any,
        todayOnly: boolean,
        includeAllPaid: boolean,
        filename: string = "report_rate.xlsx"
    ) {
        const res = await HttpService.getInstanceAxios().post(
            `/installment/report-rate-excel?todayOnly=${todayOnly}&includeAllPaid=${includeAllPaid}`,
            payload,
            { responseType: "blob" }
        );

        saveAs(
            new Blob([res.data], {type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"}),
            filename
        );
    }

    static async loadAndExportToExcelTrace(from?: string | null, to?: string | null, filename = "report_incassi.xlsx") {
        let url = `/installment/report-incassi-excel`;
        if (from && to) url += `?from=${from}&to=${to}`;

        return HttpService.getInstanceAxios()
            .get(url, {
                responseType: "blob",
            })
            .then(res => {
                //const url = URL.createObjectURL(new Blob([res.data], {type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"}));
                //window.open(url);

                saveAs(new Blob([res.data], {type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"}), filename);
            })
            .catch(() => {
                message.error("Si è verificato un errore durante l'esportazione del file Excel");
            });
    }

}
